/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmModal, SmsVerificationModal } from "@advapacs/common";
import {
  sendPhoneVerificationCode,
  sendPhoneVerificationRequest,
} from "../pages/study/ducks/studySlice";
import React from "react";
import { useAppDispatch } from "@src/config/store";
import { PhoneVerificationState } from "@advapacs/common/src/types/studies";

export const usePatientVerificationModal = ({
  phoneVerification,
  verifyingPhoneNumberSegment,
  show,
  setShow,
  onCallbackPatientVerify,
}: {
  phoneVerification?: PhoneVerificationState;
  verifyingPhoneNumberSegment?: {
    firstDigits: string;
    lastDigits: string;
  } | null;
  show: boolean;
  setShow: (show: boolean) => void;
  onCallbackPatientVerify?: () => Promise<void>;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["table", "modal", "common"]);

  const modalRenderer = useMemo(
    () => {
      if (!phoneVerification) return null;

      if (verifyingPhoneNumberSegment) {
        return (
          <SmsVerificationModal
            show={show}
            isSendingVerificationCode={phoneVerification?.loading}
            isVerifyingCode={phoneVerification?.loading}
            isVerififyCodeSuccessful={phoneVerification?.verifyCodeSuccessful}
            readyForVerification={phoneVerification?.readyVerifyCode}
            errorMessage={phoneVerification?.errorMessage}
            modalConfig={{
              title: t(
                "modal:patient_phone_verify.clinician_portal_patient_verification"
              ),
              bodyContent: (
                <div className="justify-content-center mb-2">
                  <div>
                    {t(
                      "modal:patient_phone_verify.to_view_this_sensitive_study"
                    )}
                  </div>
                  <div>
                    {t(
                      "modal:patient_phone_verify.an_sms_with_verification_code",
                      verifyingPhoneNumberSegment
                    )}
                  </div>
                </div>
              ),
            }}
            sendVerificationRequest={() =>
              dispatch(
                sendPhoneVerificationRequest({
                  pathVariables: {
                    portalId:
                      phoneVerification.verifyingStudy?.clinicianPortal?.id,
                  },
                  payload: {
                    patientId: phoneVerification.verifyingStudy?.patient?.id,
                    channel: "sms",
                  },
                })
              )
            }
            sendVerifyCode={(code) => {
              dispatch(
                sendPhoneVerificationCode({
                  pathVariables: {
                    portalId:
                      phoneVerification.verifyingStudy?.clinicianPortal?.id,
                  },
                  payload: {
                    patientId: phoneVerification.verifyingStudy?.patient?.id,
                    code,
                  },
                })
              );
            }}
            onClose={() => setShow(false)}
            callbackAction={async () => {
              if (onCallbackPatientVerify) await onCallbackPatientVerify();
            }}
          />
        );
      }
      return (
        <ConfirmModal
          show={show}
          confirm={() => setShow(false)}
          body={t(
            "common:error_messages.missing_patient_phone_number_and_require_providing_number"
          )}
          onClose={() => setShow(false)}
          confirmBtnName={t("ok")}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      show,
      phoneVerification,
      verifyingPhoneNumberSegment,
      onCallbackPatientVerify,
    ]
  );

  return { modalRenderer };
};
